/**
 * Edit one supplier-code row on the Customer Relationships table: the code a
 * customer has issued this supplier, and the currency it trades in.
 *
 * A row with no code record behind it (a relationship whose codes are all
 * archived) saves by creating one, so every row offers the same Edit.
 */

import { useEffect, useState } from 'react';
import { CustomerSupplierCode, coreAPI, apiErrorMessage } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** Currencies a supplier code can trade in. */
export const SUPPLIER_CODE_CURRENCIES = ['GBP', 'USD', 'EUR', 'PLN', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK', 'CZK', 'JPY', 'CNY', 'INR'];

/** The row being edited: its relationship, and its code record if it has one. */
export interface EditingCodeRow {
  relationshipId: number;
  customerName: string;
  code: CustomerSupplierCode | null;
  /** What the row shows when there is no code record (the relationship's own values). */
  fallbackCode: string;
  fallbackCurrency: string;
}

interface EditSupplierCodeDialogProps {
  row: EditingCodeRow | null;
  onClose: () => void;
  /** The saved record — an update of `row.code`, or a new one when it had none. */
  onSaved: (relationshipId: number, saved: CustomerSupplierCode, replacedId: number | null) => void;
}

export default function EditSupplierCodeDialog({ row, onClose, onSaved }: EditSupplierCodeDialogProps) {
  const [codeValue, setCodeValue] = useState('');
  const [currency, setCurrency] = useState('');
  const [saving, setSaving] = useState(false);

  // Re-seed on every open, so a cancelled edit never leaks into the next row.
  useEffect(() => {
    if (!row) return;
    setCodeValue(row.code ? row.code.supplier_code : row.fallbackCode);
    setCurrency(row.code ? row.code.currency : row.fallbackCurrency);
  }, [row]);

  const trimmed = codeValue.trim();
  const unchanged = row?.code
    ? trimmed === row.code.supplier_code && currency === row.code.currency
    : false;

  const save = async () => {
    if (!row) return;
    if (!trimmed) {
      toast.error('Enter the supplier code');
      return;
    }
    if (!currency) {
      toast.error('Choose the currency this code trades in');
      return;
    }
    setSaving(true);
    try {
      const res = row.code
        ? await coreAPI.updateSupplierCode(row.code.id, { supplier_code: trimmed, currency })
        : await coreAPI.createSupplierCode({ relationship: row.relationshipId, supplier_code: trimmed, currency });
      onSaved(row.relationshipId, res.data, row.code?.id ?? null);
      toast.success('Supplier code updated');
      onClose();
    } catch (err: unknown) {
      // A duplicate code on the same customer is refused server-side; show
      // what the server said so the user knows which rule they hit.
      toast.error(apiErrorMessage(err, 'Failed to update supplier code'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={row !== null} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Supplier Code</DialogTitle>
          <DialogDescription>
            The code {row?.customerName ?? 'this customer'} has issued this supplier, and the currency it trades in.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="edit-code">Supplier code</label>
            <Input id="edit-code" value={codeValue} onChange={(e) => setCodeValue(e.target.value)} placeholder="e.g. GOU002" disabled={saving} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="edit-currency">Currency</label>
            <select id="edit-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={saving} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
              <option value="">Select...</option>
              {SUPPLIER_CODE_CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || unchanged}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
